import { ComponentType } from "@angular/cdk/portal";
import { ChangeDetectorRef, Injectable, Injector, Type } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { BehaviorSubject, combineLatest, filter, startWith, Subscription, switchMap } from "rxjs";
import { tap } from "rxjs/operators";
import { IPayload, IQuestion } from "../../interface/filters";
import { AuthService } from "../../services/auth.service";
import { MyUtils } from "../../services/my-utils.service";
import { IApiService } from "./models/api/i-api-service";
import { ICol } from "./models/columns/i-col";
import { ICellComponent } from "./models/i-cell-component";
import { TableStructure } from "./models/table-structure";

@Injectable()
export class RecordsManager<T extends object> {
  tableConfig?: TableStructure<T>;
  dataSource: MatTableDataSource<T> = new MatTableDataSource<T>([]);
  paginator!: MatPaginator;
  sort!: MatSort;
  strategyService!: IApiService<T>;
  tableCdr?: ChangeDetectorRef;

  extraFilterSbj: BehaviorSubject<any> = new BehaviorSubject(undefined);

  private dataTableSubscription?: Subscription;

  get isLoading() {
    return this.strategyService?.isLoading || false;
  }

  private authSubscription?: Subscription;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private inj: Injector,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private utils: MyUtils,
    private authService: AuthService
  ) { }

  setStrategy(serviceType: Type<IApiService<T>>, questionCode?: string, question?: IQuestion) {

    this.strategyService = this.inj.get<IApiService<T>>(serviceType);
    this.authSubscription = this.authService.me$.subscribe((data) => {
      // If statically generated columns passing undefined in the user field since it's not requested in the service function
      if (data) {
        this.tableConfig = this.strategyService.genTableConfig(data, question);
      } else {
        this.tableConfig = this.strategyService.genTableConfig(undefined, question);
      }
      if (this.tableConfig) {
        this.tableConfig.questionCode = questionCode;
      }
      this.tableConfig?.cols.forEach((col) => {
        col.header = col.header !== null && col.header !== undefined ? col.header : col.columnDef;
        col.commands.forEach((c) => c.setReceiver(this));
      });
      this.tableConfig?.actions?.forEach((action) => action.setReceiver(this));
      if (this.tableConfig?.type === "clientSide") {

        this.getItems();
      }
    });
  }

  initServerSideDataSubscription() {
    this.dataTableSubscription = combineLatest([
      this.utils.settingsPayloadSelect,
      this.extraFilterSbj,
      this.sort?.sortChange.pipe(startWith(this.sort)),
      this.paginator.page.pipe(startWith(this.paginator)),
    ])
      .pipe(
        filter(([settings, extraFilters, sort, paginator]) => settings.state === "valid"),
        switchMap(([settings, extraFilters, sort, paginator]) => {
          let payload: IPayload = Object.assign({}, settings.payload);
          payload.extraFilters = extraFilters
          return this.loadServerSideTableData(
            payload,
            this.paginator.pageSize,
            this.paginator.pageIndex,
            this.sort,
            this.extraFilterSbj.value
          );
        })
      )
      .subscribe((_) => { });
  }

  unsubscribeFromDataSubscription() {
    if (this.dataTableSubscription) {
      this.dataTableSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  getItems() {
    this.dataTableSubscription = this.utils.settingsPayloadSelect
      .pipe(
        filter((val) => val !== undefined && val !== null && val.state === "valid"),
        switchMap((val) =>
          this.strategyService.getAll(
            val.payload,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            this.tableConfig
          )
        )
      )
      .subscribe((res) => {
        this.strategyService.isLoading = false;
        this.dataSource.data = res.items;
      });
  }

  loadServerSideTableData(
    filter: IPayload = this.utils.settingsPayloadValue.payload,
    pageSize: number = this.paginator.pageSize,
    pageIndex: number = this.paginator.pageIndex,
    sort: Sort = this.sort,
    extraFilters: any = this.extraFilterSbj.value
  ) {
    return this.strategyService
      .getAll(
        filter,
        pageIndex,
        pageSize,
        sort.direction.toUpperCase(),
        sort.active,
        extraFilters,
        this.tableConfig
      )
      .pipe(
        tap((res) => {
          this.dataSource.data = res.items;
          this.paginator.length = res.count;
        })
      );
    //.subscribe()
  }

  navigate(...paths: string[]) {
    this.router.navigate(paths, {
      relativeTo: this.activatedRoute,
      queryParamsHandling: "preserve",
    });
  }

  applySyncFilter(value: string, filterPredicate?: (data: T, filter: string) => boolean) {
    if (filterPredicate) {
      this.dataSource.filterPredicate = filterPredicate;
    }
    this.dataSource.filter = value.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  applyAsyncFilter(value: any) {
    //return this.loadServerSideTableData(null, undefined, undefined, value);
    this.paginator.pageIndex = 0;
    let prevValue = this.extraFilterSbj.value || {};
    this.extraFilterSbj.next(Object.assign(prevValue, value));
  }

  showConfirmDialog(
    dialogComponent: ComponentType<any>,
    dialogConfig: MatDialogConfig
  ): MatDialogRef<InstanceType<ComponentType<any>>> {
    return this.dialog.open(dialogComponent, dialogConfig);
  }

  onDeleteSuccess() {
    this.snackBar.open(this.utils.traslatei18n("Item deleted successfully"), undefined, {
      duration: 1000,
    });
    this.loadServerSideTableData().subscribe((_) => this.tableCdr?.detectChanges());
  }

  onSuccess(message = this.utils.traslatei18n("Operation completed successfully"), reload = true) {
    this.snackBar.open(message, undefined, {
      duration: 2000,
    });
    if (reload) {
      this.loadServerSideTableData().subscribe((_) => this.tableCdr?.detectChanges());
    }
  }

  onError(message = this.utils.traslatei18n("ERROR"), reload = true) {
    this.snackBar.open(message, undefined, {
      duration: 2000,
    });
    if (reload) {
      this.loadServerSideTableData().subscribe((_) => this.tableCdr?.detectChanges());
    }
  }

  showDialog<C, D>(dialogComponent: ComponentType<C>, dialogConfig: MatDialogConfig<D>) {
    return this.dialog.open<C, D>(dialogComponent, dialogConfig);
  }

  toggleColVisibility(data: string[]) {
    this.tableConfig?.cols.forEach(
      (col: ICol<T, Type<ICellComponent<T>>>) =>
        (col.visible = col.removable ? data.includes(col.columnDef) : true)
    );
    this.tableCdr?.detectChanges();
  }
}
